import React, { useContext, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import { useTour } from '@reactour/tour';
import { Iconify } from '@sdf/base';
import { useSettings } from '@sdf/base';
import { useRouter } from 'next/router';
import { useAppContext } from 'apps/t2c-fe/hooks';
import {
  getTourCompletedVersion,
  getTourStorageKey,
  getTourUserEmail,
  isTourCompletedForUser,
} from 'apps/t2c-fe/utils/tourWelcome';
import { CreditContext } from 'apps/t2c-fe/context/CreditProvider';

const RootStyle = styled('div')(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(2),
  right: theme.spacing(2),
  zIndex: 1400,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
}));

const ContinueAction = styled(Box)(() => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  cursor: 'pointer',
  borderRadius: '14px',
  padding: '8px 12px',
  minHeight: 44,
  background:
    'linear-gradient(145deg, rgba(58,50,84,0.97) 0%, rgba(34,30,48,0.97) 60%, rgba(27,24,39,0.97) 100%)',
  color: '#F5EEFF',
  border: '1px solid rgba(143, 116, 226, 0.55)',
  boxShadow: '0 12px 28px rgba(0,0,0,0.42), 0 0 0 1px rgba(143,116,226,0.22) inset',
  transition: 'transform 220ms ease, opacity 220ms ease, box-shadow 220ms ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 14px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(161,136,238,0.35) inset',
  },
  '@keyframes pulseGlow': {
    '0%': { boxShadow: '0 0 0 0 rgba(138,104,240,0.4)' },
    '70%': { boxShadow: '0 0 0 10px rgba(138,104,240,0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(138,104,240,0)' },
  },
}));

const CompassBadge = styled(Box)(() => ({
  width: 28,
  height: 28,
  borderRadius: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(145deg, #8A68F0 0%, #6541D8 100%)',
  color: '#FFFFFF',
  animation: 'pulseGlow 2s infinite',
}));

const CloseButtonBubble = styled(Box)(() => ({
  position: 'absolute',
  top: '-8px',
  right: '-8px',
  width: 24,
  height: 24,
  borderRadius: '50%',
  background: '#1F1D27',
  border: '1px solid #3A3A3A',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'transform 150ms ease, background 150ms ease',
  '&:hover': {
    transform: 'scale(1.1)',
    background: '#2A2835',
  },
}));

const enableAgentPlan = true;

const FooterButtons: React.FC = () => {
  const { setIsOpen, setCurrentStep, isOpen, currentStep } = useTour();
  const { welcomeToT2C, onChangeWelcomeTour } = useSettings();
  const { push, pathname } = useRouter();
  const { setIsDrawerOpen, userData } = useAppContext();
  const { userPlanData, agentPlanData } = useContext(CreditContext);
  const tourUserEmail = getTourUserEmail(userData);
  const tourStepKey = getTourStorageKey('tourStep', tourUserEmail);
  const tourInitializedKey = getTourStorageKey('tourInitialized', tourUserEmail);

  const [visible, setVisible] = useState(false);
  const [hiddenThisSession, setHiddenThisSession] = useState(false);

  const checkVisibility = () => {
    const skipped = currentStep !== null;

    const shouldShow = skipped && !isOpen && !isTourCompletedForUser(welcomeToT2C, tourUserEmail);

    setVisible(shouldShow);
  };

  useEffect(() => {
    checkVisibility();

    const handleTourSkipped = () => {
      checkVisibility();
      setHiddenThisSession(false);
    };

    window.addEventListener('tourSkipped', handleTourSkipped);

    return () => {
      window.removeEventListener('tourSkipped', handleTourSkipped);
    };
  }, [welcomeToT2C, isOpen]);

  const handleContinue = () => {
    setCurrentStep(currentStep);
    if (
      agentPlanData === 'agentPlan' ||
      agentPlanData === 'agentTrialPlan' ||
      (userPlanData === 'activePlan' && agentPlanData === 'noAgentPlan' && enableAgentPlan)
    ) {
      push('/ai-studio');
    } else {
      push('/text-to-image');
    }

    // if (currentStep <= 6) {
    //   push('/dashboard');
    // } else {
    // push('/text-to-image');
    // }
    if (currentStep !== (userPlanData === 'activePlan' ? 0 : 1) && currentStep !== 7) {
      setTimeout(() => {
        setIsDrawerOpen?.(true);
        localStorage.setItem(tourStepKey, currentStep.toString());
      }, 350);
    }
    localStorage.removeItem(tourInitializedKey);
    setTimeout(() => {
      setIsOpen(true);
      localStorage.setItem(tourInitializedKey, 'true');
    }, 750);

    setHiddenThisSession(true);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setHiddenThisSession(true);
    onChangeWelcomeTour(getTourCompletedVersion(tourUserEmail));
  };

  useEffect(() => {
    if (
      pathname === '/ai-studio' &&
      agentPlanData !== 'agentPlan' &&
      (userPlanData === 'activePlan' || agentPlanData === 'agentTrialPlan') &&
      enableAgentPlan
    ) {
      setCurrentStep(0);
      setHiddenThisSession(true);
      setIsOpen(true);
      localStorage.setItem(tourInitializedKey, 'true');
    }
  }, [pathname, agentPlanData]);

  if (!visible || hiddenThisSession) return null;

  return (
    <RootStyle>
      <ContinueAction
        onClick={handleContinue}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleContinue();
          }
        }}
      >
        <CompassBadge>
          <Iconify icon="mdi:compass-outline" width={16} height={16} />
        </CompassBadge>
        <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
          <Typography sx={{ color: '#FFFFFF', fontSize: '14px', fontWeight: 700 }}>
            Continue Tour
          </Typography>
          <Typography sx={{ color: 'rgba(237, 230, 255, 0.88)', fontSize: '11px' }}>
            Resume from step {(currentStep ?? 0) + 1}
          </Typography>
        </Box>
        <CloseButtonBubble onClick={(e) => handleClose(e)} aria-label="close resume">
          <Iconify icon="eva:close-fill" width={12} height={12} />
        </CloseButtonBubble>
      </ContinueAction>
    </RootStyle>
  );
};

export default FooterButtons;
